// Scale resolution: preset architectural scales, and deciding what a
// calibration on a composite sheet actually targets (the sheet, or one
// cropped floor plan). Pure math — ScaleControls and ArrangePanel consume
// it; calibrate.test.ts pins it.

import type { Pt } from "./geometry"

/** Plan pages render at this DPI (PdfPipeline#render_page's default), so an
 *  architectural scale statement converts to pixels-per-foot only through
 *  it: 1/4" = 1'-0" at 300 dpi is 75 px per foot. MUST stay in lockstep
 *  with app/services/pdf_pipeline.rb — asserted by
 *  test/integration/scale_preset_lockstep_test.rb. */
export const TAKEOFF_DPI = 300

export type ScalePreset = { label: string; inchesPerFt: number; pxPerFt: number }

const preset = (label: string, inchesPerFt: number): ScalePreset => ({
  label,
  inchesPerFt,
  pxPerFt: inchesPerFt * TAKEOFF_DPI,
})

/** The US architectural scale set, smallest to largest. A plan's title
 *  block states one of these; selecting it replaces a click-calibration.
 *  (Half-size prints halve every scale — calibration stays the fallback.) */
export const SCALE_PRESETS: ScalePreset[] = [
  preset(`1/16" = 1'-0"`, 1 / 16),
  preset(`3/32" = 1'-0"`, 3 / 32),
  preset(`1/8" = 1'-0"`, 1 / 8),
  preset(`3/16" = 1'-0"`, 3 / 16),
  preset(`1/4" = 1'-0"`, 1 / 4),
  preset(`3/8" = 1'-0"`, 3 / 8),
  preset(`1/2" = 1'-0"`, 1 / 2),
  preset(`3/4" = 1'-0"`, 3 / 4),
  preset(`1" = 1'-0"`, 1),
  preset(`1-1/2" = 1'-0"`, 1.5),
]

export const presetToFtPerPx = (p: ScalePreset): number => 1 / p.pxPerFt

/** The preset a calibration matches within ±0.5% — a careful hand
 *  calibration of a quarter-inch plan lands at ~75.2 px/ft and should read
 *  back as the scale it is. Null for genuinely custom scales. */
export function matchPreset(ftPerPx: number | null): ScalePreset | null {
  if (!ftPerPx || ftPerPx <= 0) return null
  const pxPerFt = 1 / ftPerPx
  return SCALE_PRESETS.find((p) => Math.abs(p.pxPerFt - pxPerFt) / p.pxPerFt < 0.005) ?? null
}

/** The subset of EditorSheetImage the containment test needs. */
export type CalibrationImage = {
  id: number | string
  x: number
  y: number
  scale: number
  width: number | null
  height: number | null
}

const inImage = (pt: Pt, img: CalibrationImage): boolean =>
  img.width != null &&
  img.height != null &&
  pt[0] >= img.x &&
  pt[0] <= img.x + img.width * img.scale &&
  pt[1] >= img.y &&
  pt[1] <= img.y + img.height * img.scale

export type CalibrationTarget =
  | { kind: "sheet" }
  | { kind: "crop"; imageId: number; index: number; newScale: number; factor: number }

/**
 * What does this calibration apply to?
 *
 * - The SHEET (today's behavior — sets/updates ft_per_px) when it has no
 *   calibration yet, when rooms are traced (crop transforms are locked, and
 *   with work on the sheet a recalibration means "fix the whole sheet"), or
 *   when the two points aren't attributable to exactly one crop.
 * - One CROP otherwise: the sheet's scale is established, so a dimension
 *   clicked inside a single crop rescales THAT crop until the entered
 *   distance reads true — every other crop keeps measuring correctly, and
 *   the whole sheet ends up at one uniform real-world scale. This is also
 *   how a typo gets fixed on the crop that first calibrated the sheet: it
 *   resizes rather than editing ft_per_px, which keeps every OTHER crop's
 *   truth intact.
 *
 * Mirrored lot copies always take the sheet path by construction (they carry
 * rooms), so the containment test never runs against flipped placements.
 */
export function resolveCalibrationTarget(args: {
  pts: Pt[]
  ft: number
  ftPerPx: number | null
  roomCount: number
  images: CalibrationImage[]
}): CalibrationTarget {
  const { pts, ft, ftPerPx, roomCount, images } = args
  if (pts.length !== 2 || ftPerPx == null || ftPerPx <= 0 || roomCount > 0) {
    return { kind: "sheet" }
  }
  const editable = images.filter((i) => typeof i.id === "number")
  const containing = editable.filter((i) => inImage(pts[0], i) && inImage(pts[1], i))
  // Zero crops: the points miss (or a pyramid hasn't reported its size yet).
  // Two or more: overlapping placements make the dimension ambiguous.
  if (containing.length !== 1) return { kind: "sheet" }
  const img = containing[0]
  const px = Math.hypot(pts[1][0] - pts[0][0], pts[1][1] - pts[0][1])
  if (px <= 0) return { kind: "sheet" }
  const factor = ft / (px * ftPerPx)
  if (!Number.isFinite(factor) || factor <= 0) return { kind: "sheet" }
  return {
    kind: "crop",
    imageId: img.id as number,
    index: editable.indexOf(img),
    newScale: img.scale * factor,
    factor,
  }
}

/** A crop whose source page is drawn at `preset`, placed on a sheet
 *  calibrated at `ftPerPx`: the scale factor that makes it measure true
 *  (1 ft must span 1/ftPerPx sheet-px; the source draws it as
 *  preset.pxPerFt source-px). */
export const presetCropScale = (p: ScalePreset, ftPerPx: number): number =>
  1 / ftPerPx / p.pxPerFt
